<template>
  <SettingsForm :initial-values="initialValues">
    <SettingsFormItem :title="$t('common.gender')">
      <SelectInputField
        name="gender"
        :placeholder="$t('common.your_gender')"
        :options="genders"
        @input="(value) => setGender(value)"
      />
    </SettingsFormItem>
    <SettingsFormItem :title="$t('common.birthday_date')" required>
      <div class="calendar-container">
        <div @click="showCalendar">
          <TextInputField
            v-model="form.date_of_birth"
            name="date_of_birth"
            rules="required"
            placeholder="1900-01-01"
            @input="onBirthdateChange"
          />
        </div>
        <v-date-picker
          v-if="calendarEnabled"
          ref="calendar"
          :model-value="form.date_of_birth"
          locale="lt"
          color="red"
          is-expanded
          @dayclick="onDayClick"
        />
      </div>
    </SettingsFormItem>
    <SettingsFormItem
      v-if="hasPersonalCode"
      :title="$t('common.personal_code')"
    >
      <TextInputField
        name="personal_code"
        rules="digits:11"
        :placeholder="$t('common.personal_code_placeholder')"
        @input="(event) => onPersonalCodeInput(event.target.value)"
        @blur="onPersonalCodeBlur"
      />
    </SettingsFormItem>
  </SettingsForm>
</template>

<script setup>
import SelectInputField from "../SelectInputField.vue";
import { computed, defineEmits, defineProps, ref } from "vue";
import TextInputField from "../TextInputField.vue";
import SettingsForm from "@/components/form/dashboard/components/SettingsForm.vue";
import SettingsFormItem from "@/components/form/dashboard/components/SettingsFormItem.vue";
import { onClickOutside } from "@vueuse/core";
import debounce from "lodash.debounce";

const emit = defineEmits(["submit"]);
const props = defineProps({ initialValues: Object, loading: Boolean });

const calendar = ref(null);
const form = ref({
  date_of_birth: props.initialValues.date_of_birth,
});
const calendarEnabled = ref(false);

// The field only exists when the account this screen edits carries a personal
// code (bound default-environment users); it is absent for Moodle-provider /
// unbound accounts, so the key drives the whole feature.
const hasPersonalCode = computed(() => "personal_code" in props.initialValues);

const genders = [
  {
    name: "Vyras",
    value: "male",
  },
  {
    name: "Moteris",
    value: "female",
  },
];

const setGender = (value) => {
  if (!value || value === props.initialValues["gender"]) {
    return;
  }

  emit("submit", { gender: value });
};
const setBirthdate = (value) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(value)) {
    return;
  }
  form.value.date_of_birth = value;
  emit("submit", { date_of_birth: value });
};
const showCalendar = () => {
  calendarEnabled.value = !calendarEnabled.value;
};
const onDayClick = (selectedDay) => {
  const month =
    selectedDay.month < 10 ? `0${selectedDay.month}` : selectedDay.month;
  const day = selectedDay.day < 10 ? `0${selectedDay.day}` : selectedDay.day;
  setBirthdate(`${selectedDay.year}-${month}-${day}`);
  calendarEnabled.value = false;
};
const onBirthdateChange = debounce(
  () => setBirthdate(form.value.date_of_birth),
  1000,
);

// Personal code is shown unmasked. Emitting is split in two on purpose.
//
// Typing only ever SETS a complete code. Treating an empty value as a
// debounced change would save a clear one second after the user deletes the
// old code to type a new one — silently wiping the code, spending one of the
// ten hourly changes and writing a "clear" the user never asked for. Anyone
// interrupted mid-edit would simply lose it.
//
// Clearing stays possible, but it has to be deliberate: the field is left
// empty and focus moves away. TextInputField forwards `blur` without a
// payload, so the last typed value is kept here.
const personalCodeDraft = ref(null);

const emitPersonalCode = (next) => {
  if (next === (props.initialValues.personal_code ?? "")) {
    return;
  }
  emit("submit", { personal_code: next });
};

const onPersonalCodeChange = debounce((value) => {
  if (!/^\d{11}$/.test(value)) {
    return;
  }
  emitPersonalCode(value);
}, 1000);

const onPersonalCodeInput = (value) => {
  personalCodeDraft.value = (value ?? "").trim();
  onPersonalCodeChange(personalCodeDraft.value);
};

const onPersonalCodeBlur = () => {
  if (personalCodeDraft.value !== "") {
    return;
  }
  // A pending set would fire after the clear and undo it.
  onPersonalCodeChange.cancel();
  emitPersonalCode("");
};

onClickOutside(calendar, () => (calendarEnabled.value = false));
</script>

<style lang="scss" scoped>
.calendar-container {
  position: relative;
}

:deep(.vc-container) {
  border: none;
  position: absolute;
  box-shadow: -2px 2px 12px 0px #1b214a1a;
  background-color: $color-white;
  z-index: 2;
}

:deep(.vc-day) {
  color: $color-primary;
  padding: 0 5px;
}

:deep(.vc-day.weekday-7),
:deep(.vc-day.weekday-1) {
  color: red;
}

:deep(.vc-day-content) {
  font-weight: 500;
  font-size: $font-size-xs;
  font-family: $font-family;
  width: 32px;
  height: 32px;
}

:deep(.vc-day-content) {
  font-weight: 500;
  font-size: $font-size-xs;
  font-family: $font-family;
  width: 32px;
  height: 32px;
  line-height: 32px;
}

:deep(.vc-weekday) {
  color: $color-menu-item;
  font-size: $font-size-xs;
  font-family: $font-family;
  font-weight: 500;
}

:deep(.vc-title) {
  color: $color-primary;
  background: transparent;
  font-family: $font-family;
  font-size: $font-size-xs;
  font-weight: 500;
}

:deep(.vc-arrow) {
  background: transparent;
  width: 12px;
}
</style>
